import React, { useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { createFilterOptions } from '@material-ui/lab/Autocomplete';
import { makeStyles, Tooltip } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { Skeleton } from '@material-ui/lab';
import MoreResult from '~/modules/common/components/MoreResult';
import { MORE_AVAILABLE_OPTION_ID } from '~/modules/common/components/SearchAutocomplete/SearchAutocomplete';
import { useHasPermission } from '~/modules/common/permissions';
import { AutocompleteInput } from '~/modules/common/components/SearchAutocomplete';
import CreatableAutocomplete from '~/modules/common/components/SearchAutocomplete/CreatableAutocomplete';
import {
  AddRoleDialog,
  RoleDropdownItem
} from '~/modules/resourcing/common/components/SearchableRoleDropDown';
import { useDialogState } from '~/modules/common/hooks/useDialogState';
import useGroupedProjectRateCardRoles from './useGroupedProjectRateCardRoles';
import GroupedListSubheader from './GroupedListSubheader';

const useStyles = makeStyles(theme => ({
  roleContainer: {}
}));

const filter = createFilterOptions();
const groupBy = option => option.groupLabelId;
const getOptionLabel = option => option.displayText;
const getOptionSelected = (option, value) => option.id === value.id;
const getOptionDisabled = option => option.id === MORE_AVAILABLE_OPTION_ID;

export const RateTableRolesDropdown = ({
  autoFocus,
  classes: classesOverride,
  className,
  disableClearable,
  disabled,
  label,
  onAddRoleDialogCancelButtonClick,
  onChange,
  placeholder,
  projectId,
  value,
  variant,
  error,
  propOverrides,
  rateTableDimensionLoading,
  inputRef
}) => {
  const intl = useIntl();
  const classes = useStyles({ classes: classesOverride });

  const canCreateRole = useHasPermission({
    actionUri: 'urn:replicon:project-role-action:edit-project-role'
  });

  const [searchText, setSearchText] = useState('');
  const { open, closeDialog: onDialogClose, openDialog } = useDialogState();
  const [newRoleName, setNewRoleName] = useState('');

  const {
    roleOptions,
    isLoading,
    hasMore,
    refetch: refetchRoles
  } = useGroupedProjectRateCardRoles({
    projectId,
    searchText: value ? '' : searchText
  });

  const handleInputChange = useCallback((_, v) => setSearchText(v), []);

  const openDialogAndSetInitialName = useCallback(
    name => {
      setNewRoleName(name);
      openDialog();
    },
    [openDialog, setNewRoleName]
  );

  const handleRoleCreated = useCallback(() => {
    refetchRoles();
  }, [refetchRoles]);

  const filterOptions = useCallback(
    (options, params) => {
      const filtered = filter(options, params);

      if (
        canCreateRole &&
        params.inputValue !== '' &&
        !options.some(o => o.displayText === params.inputValue)
      ) {
        filtered.push({
          isCreatable: true,
          id: params.inputValue,
          displayText: params.inputValue
        });
      }

      return filtered;
    },
    [canCreateRole]
  );

  const renderGroup = useCallback(
    params => <GroupedListSubheader params={params} />,
    []
  );

  const renderOption = useCallback(
    option => {
      if (rateTableDimensionLoading)
        return <Skeleton variant="text" width="80%" height={30} />;

      return option.id === MORE_AVAILABLE_OPTION_ID ? (
        <MoreResult message={option.displayText} />
      ) : (
        <RoleDropdownItem
          onCreate={openDialogAndSetInitialName}
          option={option}
          showTooltip={false}
        />
      );
    },
    [openDialogAndSetInitialName, rateTableDimensionLoading]
  );

  const handleRoleChange = useCallback(role => onChange(role), [onChange]);

  const enrichedValue = useMemo(() => {
    if (!value) return null;

    return (
      roleOptions.find(opt => opt.uri === value.id) || {
        ...value,
        isInRateTable: false,
        groupLabelId: 'rateCard.dropdown.otherRoles'
      }
    );
  }, [value, roleOptions]);

  const selectedRole = isLoading ? null : enrichedValue;

  const { helperText, tooltip } = propOverrides?.getHelperAndTooltip
    ? propOverrides.getHelperAndTooltip({ intl, selectedRole })
    : { helperText: '', tooltip: '' };

  const inputLabelPropsOverride = useMemo(() => ({ shrink: true }), []);

  const renderInput = useCallback(
    params => (
      <AutocompleteInput
        InputLabelPropsOverride={value ? inputLabelPropsOverride : null}
        alwaysShowHelperText={
          rateTableDimensionLoading ||
          isLoading ||
          (!error && !isLoading && !!helperText)
        }
        autoFocus={autoFocus}
        className={className}
        helperText={
          rateTableDimensionLoading || isLoading ? (
            <Skeleton width="40%" height={20} />
          ) : (
            helperText
          )
        }
        label={label}
        placeholder={placeholder}
        variant={variant}
        inputRef={inputRef}
        {...params}
      />
    ),
    [
      rateTableDimensionLoading,
      error,
      isLoading,
      helperText,
      autoFocus,
      className,
      label,
      placeholder,
      variant,
      inputRef
    ]
  );

  return (
    <>
      <Tooltip
        className={classes.roleContainer}
        disableHoverListener={!tooltip || isLoading}
        placement="top"
        title={tooltip}
      >
        <div>
          <CreatableAutocomplete
            canAddNewOption={canCreateRole}
            classes={classes}
            clearOnBlur
            dataQeId="RateTableRolesDropdown"
            disableClearable={disableClearable}
            disabled={disabled}
            dropdownListId="RateTableRoleDropdownListBox"
            filterOptions={filterOptions}
            fullWidth
            getOptionDisabled={getOptionDisabled}
            getOptionLabel={getOptionLabel}
            getOptionSelected={getOptionSelected}
            groupBy={groupBy}
            hasMore={hasMore}
            loading={isLoading}
            loadingText={intl.formatMessage({
              id: 'rateCard.loading'
            })}
            onAddClick={openDialog}
            onChange={handleRoleChange}
            onInputChange={handleInputChange}
            options={roleOptions}
            optionTypeText={intl.formatMessage({
              id: 'moreOptions.roles'
            })}
            renderGroup={renderGroup}
            renderInput={renderInput}
            renderOption={renderOption}
            value={enrichedValue}
          />
        </div>
      </Tooltip>
      <AddRoleDialog
        canUserEditSkillAssignment
        initialName={newRoleName}
        onCancel={onAddRoleDialogCancelButtonClick}
        onChange={handleRoleChange}
        onClose={onDialogClose}
        onRoleCreated={handleRoleCreated}
        open={open}
      />
    </>
  );
};

RateTableRolesDropdown.propTypes = {
  autoFocus: PropTypes.bool,
  classes: PropTypes.object,
  className: PropTypes.string,
  disableClearable: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  label: PropTypes.string,
  onAddRoleDialogCancelButtonClick: PropTypes.func,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  projectId: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  variant: PropTypes.string,
  propOverrides: PropTypes.object,
  rateTableDimensionLoading: PropTypes.bool,
  inputRef: PropTypes.object
};

export default RateTableRolesDropdown;
